import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Input } from "@ews-admin/global-design-system";
import { licenceBillingConfigApi } from "../../services/licenceBillingConfig.api";

export const LicenceBillingDiscountConfig: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [fullPaymentDiscount, setFullPaymentDiscount] = useState("0");
  const [monthlyCommitmentDiscount, setMonthlyCommitmentDiscount] =
    useState("0");

  useEffect(() => {
    licenceBillingConfigApi
      .get()
      .then(data => {
        setFullPaymentDiscount(
          String(data.yearlyFullPaymentDiscountPercentage)
        );
        setMonthlyCommitmentDiscount(
          String(data.yearlyMonthlyCommitmentDiscountPercentage)
        );
      })
      .catch(() =>
        setErrorMsg(t("admin.pricing.yearlyBillingDiscount.errorLoading"))
      )
      .finally(() => setLoading(false));
  }, [t]);

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg(null);
    try {
      const updated = await licenceBillingConfigApi.update({
        yearlyFullPaymentDiscountPercentage: Number(fullPaymentDiscount),
        yearlyMonthlyCommitmentDiscountPercentage: Number(
          monthlyCommitmentDiscount
        ),
      });
      setFullPaymentDiscount(
        String(updated.yearlyFullPaymentDiscountPercentage)
      );
      setMonthlyCommitmentDiscount(
        String(updated.yearlyMonthlyCommitmentDiscountPercentage)
      );
      toast.success(t("admin.pricing.yearlyBillingDiscount.updateSuccess"));
    } catch {
      toast.error(t("admin.pricing.yearlyBillingDiscount.updateError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6 mb-6">
      <h3 className="text-base font-bold text-gray-900">
        {t("admin.pricing.yearlyBillingDiscount.title")}
      </h3>
      <p className="text-sm text-gray-500 mt-1">
        {t("admin.pricing.yearlyBillingDiscount.subtitle")}
      </p>
      <p className="text-sm text-gray-500 mt-1 mb-5">
        {t("admin.pricing.yearlyBillingDiscount.fixedDurationsNote")}
      </p>

      {errorMsg && (
        <div className="p-3 mb-5 text-red-700 bg-red-50 rounded-lg border border-red-200 text-sm">
          {errorMsg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-8 h-8 rounded-full border-b-2 border-primary-600 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("admin.pricing.yearlyBillingDiscount.fullPaymentLabel")}
            </label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={fullPaymentDiscount}
              onChange={e => setFullPaymentDiscount(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-400">
              {t("admin.pricing.yearlyBillingDiscount.fullPaymentNote")}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("admin.pricing.yearlyBillingDiscount.monthlyCommitmentLabel")}
            </label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={monthlyCommitmentDiscount}
              onChange={e => setMonthlyCommitmentDiscount(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-400">
              {t("admin.pricing.yearlyBillingDiscount.monthlyCommitmentNote")}
            </p>
          </div>
        </div>
      )}

      {!loading && (
        <div className="flex justify-end mt-6">
          <Button onClick={handleSave} loading={saving} disabled={saving}>
            {t("admin.pricing.saveChanges")}
          </Button>
        </div>
      )}
    </div>
  );
};
